import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Handshake, LogOut, UserCircle, Store, DollarSign, Send, Link2, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import useAuthStore from '../lib/auth-store';
import { apiFetch, fmtDate } from '../components/merchant/attributionApi';
import { ReferredTable, CommissionLines } from './ReferralPartnerPage';

const PROFILE = [['company', 'Company'], ['phone', 'Phone'], ['website', 'Website'], ['territory', 'Territory'], ['preferred_contact', 'Preferred contact']];

function ProfileTab({ me, token, onSaved }) {
  const [f, setF] = useState(() => ({ ...Object.fromEntries(PROFILE.map(([k]) => [k, me.profile?.[k] ?? me[k] ?? ''])), bio: me.profile?.bio || '' }));
  const save = async () => {
    try { onSaved(await apiFetch(token, '/affiliate/me', 'PUT', f)); toast.success('Profile saved'); } catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="p-6 space-y-4" data-testid="portal-profile">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PROFILE.map(([k, label]) => (
          <label key={k} className="space-y-1"><span className="text-xs text-muted-foreground">{label}</span>
            <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`portal-profile-${k}`} /></label>
        ))}
      </div>
      <label className="space-y-1 block"><span className="text-xs text-muted-foreground">About you</span>
        <Textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="portal-profile-bio" /></label>
      <Button onClick={save} data-testid="portal-profile-save">Save profile</Button>
    </Card>
  );
}

const EMPTY_LEAD = { business_name: '', contact_name: '', phone: '', email: '', city: '', state: '', pain_point: '' };

function LeadsTab({ token }) {
  const [leads, setLeads] = useState([]);
  const [f, setF] = useState(EMPTY_LEAD);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => apiFetch(token, '/affiliate/leads').then(setLeads).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await apiFetch(token, '/affiliate/leads', 'POST', Object.fromEntries(Object.entries(f).filter(([, v]) => v)));
      toast.success('Lead submitted — your partner manager has been notified');
      setF(EMPTY_LEAD); load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const field = (k, ph, type = 'text', req = false) => <Input type={type} placeholder={ph} required={req} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`portal-lead-${k}`} />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-6">
        <form onSubmit={submit} className="space-y-3" data-testid="portal-lead-form">
          <h3 className="font-semibold text-sm">Refer a business</h3>
          {field('business_name', 'Business name', 'text', true)}
          <div className="grid grid-cols-2 gap-2">{field('contact_name', 'Contact name', 'text', true)}{field('phone', 'Phone', 'tel', true)}</div>
          {field('email', 'Email', 'email', true)}
          <div className="grid grid-cols-2 gap-2">{field('city', 'City')}{field('state', 'State')}</div>
          <Textarea placeholder="Anything we should know?" value={f.pain_point} onChange={(e) => setF({ ...f, pain_point: e.target.value })} data-testid="portal-lead-notes" />
          <Button type="submit" disabled={busy} data-testid="portal-lead-submit"><Send className="w-4 h-4 mr-1" /> Submit lead</Button>
        </form>
      </Card>
      <Card className="p-6" data-testid="portal-lead-list">
        <h3 className="font-semibold text-sm mb-3">Your submitted leads</h3>
        {!leads.length ? <p className="text-sm text-muted-foreground">Nothing submitted yet</p> : leads.map((l) => (
          <div key={l.id} className="flex justify-between py-2 border-b last:border-0 text-sm">
            <span>{l.business_name} <span className="text-xs text-muted-foreground">· {fmtDate(l.created_at)}</span></span>
            <Badge variant="outline" className="capitalize">{l.status}</Badge>
          </div>
        ))}
      </Card>
    </div>
  );
}

function ReferralLinkTab({ token }) {
  const [info, setInfo] = useState(null);
  useEffect(() => { apiFetch(token, '/affiliate/referral-link').then(setInfo).catch((e) => toast.error(e.message)); }, [token]);
  if (!info) return null;
  if (!info.form_slug) return <Card className="p-6 text-sm text-muted-foreground" data-testid="portal-link-unavailable">Your partner manager hasn't published a referral form yet.</Card>;
  const url = `${window.location.origin}/forms/${info.form_slug}?ref=${info.code}`;
  return (
    <Card className="p-6 flex flex-col md:flex-row gap-6 items-start" data-testid="portal-referral-link">
      <div className="bg-white p-3 rounded-lg border" data-testid="portal-referral-qr"><QRCodeSVG value={url} size={160} /></div>
      <div className="space-y-3 flex-1 min-w-0">
        <p className="text-sm">Share this link or QR code. Every business that applies through it is credited to you automatically.</p>
        <div className="flex gap-2">
          <Input readOnly value={url} className="text-xs" data-testid="portal-referral-url" />
          <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copied'); }} data-testid="portal-referral-copy"><Copy className="w-4 h-4" /></Button>
        </div>
        <div className="flex gap-6 text-sm">
          <div><p className="text-xs text-muted-foreground">Code</p><p className="font-mono">{info.code}</p></div>
          <div><p className="text-xs text-muted-foreground">Clicks</p><p className="font-semibold" data-testid="portal-referral-clicks">{info.clicks}</p></div>
          <div><p className="text-xs text-muted-foreground">Leads credited</p><p className="font-semibold" data-testid="portal-referral-leads">{info.leads}</p></div>
        </div>
      </div>
    </Card>
  );
}

export default function AffiliatePortalPage() {
  const { token, logout } = useAuthStore();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [refs, setRefs] = useState([]);
  const [comm, setComm] = useState(null);
  useEffect(() => {
    apiFetch(token, '/affiliate/me').then(setMe).catch((e) => toast.error(e.message));
    apiFetch(token, '/affiliate/referrals').then(setRefs).catch(() => {});
    apiFetch(token, '/affiliate/commissions').then(setComm).catch(() => {});
  }, [token]);
  const out = async () => { await logout(); navigate('/login'); };
  return (
    <div className="min-h-screen bg-background" data-testid="affiliate-portal">
      <header className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold"><Handshake className="w-5 h-5 text-primary" /> Partner Portal</div>
          <Button variant="ghost" size="sm" onClick={out} data-testid="portal-logout"><LogOut className="w-4 h-4 mr-1" /> Sign out</Button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {me && (
          <div>
            <h1 className="text-3xl font-bold" data-testid="portal-welcome">Welcome, {me.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {me.referral_count} referred merchant{me.referral_count !== 1 ? 's' : ''}
              {me.partner_manager && <> · Partner manager: {me.partner_manager.name}</>}
            </p>
          </div>
        )}
        <Tabs defaultValue="referrals">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="referrals" data-testid="portal-tab-referrals"><Store className="w-4 h-4 mr-1" /> Referrals</TabsTrigger>
            <TabsTrigger value="commission" data-testid="portal-tab-commission"><DollarSign className="w-4 h-4 mr-1" /> Commission</TabsTrigger>
            <TabsTrigger value="link" data-testid="portal-tab-link"><Link2 className="w-4 h-4 mr-1" /> My link</TabsTrigger>
            <TabsTrigger value="leads" data-testid="portal-tab-leads"><Send className="w-4 h-4 mr-1" /> Submit leads</TabsTrigger>
            <TabsTrigger value="profile" data-testid="portal-tab-profile"><UserCircle className="w-4 h-4 mr-1" /> Profile</TabsTrigger>
          </TabsList>
          <TabsContent value="referrals"><Card className="p-6"><ReferredTable rows={refs} testId="portal-referrals" /></Card></TabsContent>
          <TabsContent value="commission">
            <Card className="p-6 space-y-4">
              {comm?.current_agreement ? (
                <p className="text-sm" data-testid="portal-agreement">Your agreement: <b>{comm.current_agreement.residual_pct}%</b> residual share · <b>${comm.current_agreement.bounty_per_approved_mid}</b> per approved MID</p>
              ) : <p className="text-sm text-muted-foreground" data-testid="portal-agreement-none">Your compensation agreement hasn't been set yet.</p>}
              <CommissionLines lines={comm?.lines} totals={comm?.totals} testId="portal-commissions" />
            </Card>
          </TabsContent>
          <TabsContent value="link"><ReferralLinkTab token={token} /></TabsContent>
          <TabsContent value="leads"><LeadsTab token={token} /></TabsContent>
          <TabsContent value="profile">{me && <ProfileTab me={me} token={token} onSaved={setMe} />}</TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
